import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Clock, Copy, Check, QrCode, CreditCard, Smartphone, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan') || 'starter';
  
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('pix'); // 'pix' ou 'card'
  const [paymentData, setPaymentData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  const [stripeLoading, setStripeLoading] = useState(false);

  const plans = {
    starter: { name: 'Plano Starter', price: 297, features: ['50 links', '25 conteúdos IA', 'Suporte email'] },
    pro: { name: 'Plano Pro', price: 797, features: ['500 links', '250 conteúdos IA', 'Suporte prioritário'] },
    enterprise: { name: 'Plano Enterprise', price: 997, features: ['Links ilimitados', 'IA ilimitada', 'Suporte dedicado'] }
  };

  const selectedPlan = plans[planId] || plans.starter;

  useEffect(() => {
    if (paymentMethod === 'pix') {
      generatePixPayment();
    }
  }, [planId, paymentMethod]);

  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  useEffect(() => {
    if (!paymentData || paymentMethod !== 'pix') return;
    
    const checkInterval = setInterval(async () => {
      await checkPaymentStatus();
    }, 5000);

    return () => clearInterval(checkInterval);
  }, [paymentData, paymentMethod]);

  const generatePixPayment = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      const response = await axios.post(`${API}/payments/create-pix`, {
        plan_id: planId,
        amount: selectedPlan.price,
        user_email: user.email
      });
      
      setPaymentData(response.data);
    } catch (error) {
      console.error('Error generating PIX:', error);
      setPaymentData({
        pix_code: `00020126580014BR.GOV.BCB.PIX0136${Date.now()}5204000053039865802BR5925MONETIZEMASTER`,
        qr_code_base64: null,
        payment_id: `PAY_${Date.now()}`,
        expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
        is_real: false
      });
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    if (!paymentData?.payment_id) return;
    
    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/payments/check/${paymentData.payment_id}`);
      
      if (response.data.status === 'paid' || response.data.status === 'approved') {
        toast.success('Pagamento confirmado! 🎉');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        user.payment_status = 'paid';
        user.subscription_plan = planId;
        localStorage.setItem('user', JSON.stringify(user));
        navigate(`/upsell?plan=${planId}&payment_id=${paymentData.payment_id}`);
      }
    } catch (error) {
      // Silencioso
    } finally {
      setCheckingPayment(false);
    }
  };

  const copyPixCode = () => {
    if (paymentData?.pix_code) {
      navigator.clipboard.writeText(paymentData.pix_code);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  // Nova função para Stripe Checkout
  const handleStripeCheckout = async () => {
    setStripeLoading(true);
    
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (!user.email) {
        toast.error('Por favor, faça login primeiro');
        navigate('/login');
        return;
      }
      
      const response = await axios.post(`${API}/payments/create-stripe-checkout`, {
        plan_id: planId,
        user_email: user.email,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        // Redirecionar para o Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Erro ao criar sessão de pagamento');
      }
    } catch (error) {
      console.error('Stripe checkout error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao iniciar pagamento com cartão');
    } finally {
      setStripeLoading(false);
    }
  };

  const simulatePayment = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      await axios.post(`${API}/payments/simulate-approval`, {
        plan_id: planId,
        amount: selectedPlan.price,
        user_email: user.email
      });
      
      user.payment_status = 'paid';
      user.subscription_plan = planId;
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.removeItem('pending_payment');
      
      toast.success('Pagamento confirmado! 🎉');
      navigate(`/upsell?plan=${planId}&payment_id=${paymentData?.payment_id || 'demo'}`);
    } catch (error) {
      navigate(`/upsell?plan=${planId}&payment_id=${paymentData?.payment_id || 'demo'}`);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Botão Voltar */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Voltar
        </button>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">💳 Finalizar Compra</h1>
            <p className="text-gray-400">Escolha sua forma de pagamento</p>
          </div>

          {/* Resumo do Pedido */}
          <div className="bg-white/5 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-semibold text-white mb-4">{selectedPlan.name}</h2>
            <ul className="space-y-2 mb-4">
              {selectedPlan.features.map((feature, i) => (
                <li key={i} className="text-gray-300 flex items-center">
                  <Check className="h-4 w-4 text-green-400 mr-2" />
                  {feature}
                </li>
              ))}
            </ul>
            <div className="border-t border-white/10 pt-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Total:</span>
                <span className="text-3xl font-bold text-white">R$ {selectedPlan.price},00</span>
              </div>
            </div>
          </div>

          {/* Timer */}
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 text-center">
            <div className="flex items-center justify-center text-red-400 mb-2">
              <Clock className="h-5 w-5 mr-2" />
              <span className="font-semibold">Oferta expira em:</span>
            </div>
            <div className="text-3xl font-bold text-white">{formatTime(timeLeft)}</div>
          </div>

          {/* Seleção de Método de Pagamento */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => setPaymentMethod('pix')}
              className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center ${
                paymentMethod === 'pix'
                  ? 'border-green-500 bg-green-500/20'
                  : 'border-white/20 bg-white/5 hover:border-white/40'
              }`}
            >
              <Smartphone className={`h-8 w-8 mb-2 ${paymentMethod === 'pix' ? 'text-green-400' : 'text-gray-400'}`} />
              <span className={`font-semibold ${paymentMethod === 'pix' ? 'text-green-400' : 'text-white'}`}>PIX</span>
              <span className="text-xs text-gray-400 mt-1">Aprovação instantânea</span>
            </button>
            
            <button
              onClick={() => setPaymentMethod('card')}
              className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center ${
                paymentMethod === 'card'
                  ? 'border-blue-500 bg-blue-500/20'
                  : 'border-white/20 bg-white/5 hover:border-white/40'
              }`}
            >
              <CreditCard className={`h-8 w-8 mb-2 ${paymentMethod === 'card' ? 'text-blue-400' : 'text-gray-400'}`} />
              <span className={`font-semibold ${paymentMethod === 'card' ? 'text-blue-400' : 'text-white'}`}>Cartão</span>
              <span className="text-xs text-gray-400 mt-1">Crédito ou Débito</span>
            </button>
          </div>

          {/* Conteúdo baseado no método selecionado */}
          {paymentMethod === 'pix' ? (
            /* PIX Payment */
            loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
                <p className="text-gray-400">Gerando código PIX...</p>
              </div>
            ) : paymentData ? (
              <div className="space-y-6">
                {paymentData.is_real && (
                  <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-2 text-center">
                    <span className="text-green-400 text-sm font-semibold">✅ PIX Real - Mercado Pago</span>
                  </div>
                )}
                
                <div className="bg-white rounded-xl p-6 text-center">
                  {paymentData.qr_code_base64 ? (
                    <img 
                      src={`data:image/png;base64,${paymentData.qr_code_base64}`}
                      alt="QR Code PIX"
                      className="h-48 w-48 mx-auto mb-4"
                    />
                  ) : (
                    <QrCode className="h-32 w-32 mx-auto text-gray-800 mb-4" />
                  )}
                  <p className="text-gray-600 text-sm">Escaneie o QR Code ou copie o código abaixo</p>
                </div>

                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-2">Código PIX (Copia e Cola):</p>
                  <div className="flex items-center gap-2">
                    <input 
                      type="text"
                      value={paymentData.pix_code}
                      readOnly
                      className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-sm truncate"
                    />
                    <button
                      onClick={copyPixCode}
                      className={`p-3 rounded-lg transition-all ${
                        copied 
                          ? 'bg-green-500 text-white' 
                          : 'bg-yellow-500 hover:bg-yellow-600 text-black'
                      }`}
                    >
                      {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="text-center">
                  {checkingPayment ? (
                    <p className="text-yellow-400 animate-pulse">🔄 Verificando pagamento...</p>
                  ) : (
                    <p className="text-gray-400">Aguardando confirmação do pagamento...</p>
                  )}
                </div>

                <button
                  onClick={simulatePayment}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-lg font-bold text-lg hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50"
                >
                  {loading ? 'Processando...' : '✅ SIMULAR PAGAMENTO APROVADO'}
                </button>
                <p className="text-center text-gray-500 text-xs">
                  * Botão de simulação para testes
                </p>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-red-400">Erro ao gerar pagamento. Tente novamente.</p>
                <button 
                  onClick={generatePixPayment}
                  className="mt-4 bg-yellow-500 text-black px-6 py-2 rounded-lg font-semibold"
                >
                  Tentar Novamente
                </button>
              </div>
            )
          ) : (
            /* Card Payment - Stripe Checkout */
            <div className="space-y-6">
              <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4 text-center">
                <span className="text-blue-400 text-sm font-semibold">💳 Pagamento Seguro via Stripe</span>
                <p className="text-gray-400 text-xs mt-1">Você será redirecionado para uma página segura</p>
              </div>

              <div className="bg-white/5 rounded-xl p-6 text-center">
                <CreditCard className="h-16 w-16 mx-auto text-blue-400 mb-4" />
                <h3 className="text-white text-lg font-semibold mb-2">Pague com Cartão de Crédito ou Débito</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Ao clicar no botão abaixo, você será redirecionado para a página segura do Stripe 
                  para concluir seu pagamento.
                </p>
                
                <div className="flex items-center justify-center gap-3 mb-4">
                  <img src="https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4bdb47b03ac81d9945bfe.svg" alt="Visa" className="h-8" />
                  <img src="https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg" alt="Mastercard" className="h-8" />
                  <img src="https://js.stripe.com/v3/fingerprinted/img/amex-a49b82f46c5cd6a96a6e418a6ca1717c.svg" alt="Amex" className="h-8" />
                </div>
              </div>

              <button
                onClick={handleStripeCheckout}
                disabled={stripeLoading}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-4 rounded-lg font-bold text-lg hover:from-blue-600 hover:to-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {stripeLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Redirecionando...
                  </>
                ) : (
                  <>
                    <ExternalLink className="h-5 w-5" />
                    PAGAR R$ {selectedPlan.price},00 COM CARTÃO
                  </>
                )}
              </button>

              <p className="text-center text-gray-500 text-xs">
                🔒 Seus dados estão protegidos com criptografia SSL
              </p>
            </div>
          )}
        </div>

        {/* Garantias */}
        <div className="mt-6 text-center text-gray-400 text-sm">
          <p>🔒 Pagamento 100% seguro | 💰 Garantia de 7 dias | 📱 Acesso imediato</p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
